import React, { useState, useEffect, useRef, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBookOpen,
  faChevronDown,
  faChevronUp,
  faCommentsAlt,
  faChevronLeft,
  faArrowLeft,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { ICourseModel } from 'models/course.model'
import { IAssignedLessonModel } from 'models/assignedLesson.model'
import { AssignedLessonStateEnum } from 'enum'
import { ILessonModel } from 'models/lesson.model'
import { useCourse } from 'contexts'
import { useAccess } from 'contexts/AccessContext'
import { IEvaluationResume } from 'models/evaluation.model'
import LessonNavigation from './components/lesson-navigation/LessonNavigation'
import styles from './CourseSidebar.module.scss'
import { BackButton, CourseName, LessonsCount } from './styled'
import EvaluationDetails from './evalution'

export interface Props {
  course: ICourseModel
  lessons: ILessonModel[]
  courseProgress: IAssignedLessonModel[]
  currentLesson: ILessonModel
  handleGoToLesson: (lesson: ILessonModel, lessonIndex: number) => void
  showChat?: any
  chatActive: boolean
  newMessages: number
  hideResume?: boolean
  onEvaluation?: boolean
  setOnEvaluation?: any
  evaluation: IEvaluationResume | null
  onIncompleteMultimedia?(): void
  onIncompleteEvaluations?(): void
  onIncompleteTasks?(): void
}

const ALLOW_SKIP_TO_NEXT_LESSON = false

const CourseSidebard: React.FC<Props> = ({
  course,
  lessons,
  courseProgress,
  currentLesson,
  handleGoToLesson,
  showChat,
  chatActive,
  newMessages,
  hideResume = false,
  onEvaluation = false,
  setOnEvaluation = undefined,
  evaluation,
  onIncompleteMultimedia,
  onIncompleteEvaluations,
  onIncompleteTasks,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const history = useHistory()
  const { isEndUser } = useAccess()
  const lessonsCountRef = useRef(0)
  const [lessonsContainerHeight, setLessonsContainerHeight] = useState(141)
  const courseProgressByLesson = useMemo(
    () =>
      new Map(
        courseProgress.map((assignedLesson) => [
          assignedLesson.lessonId,
          assignedLesson,
        ])
      ),
    [courseProgress]
  )

  useEffect(() => {
    if (lessons) lessonsCountRef.current = lessons.length
  }, [lessons])

  const endUser = isEndUser()

  useEffect(() => {
    let newHeight = 141
    if (!hideResume) newHeight += 38
    if (endUser) newHeight += 38
    setLessonsContainerHeight(newHeight)
  }, [endUser, hideResume])

  const handleClick = (lesson: ILessonModel, index: number) => {
    setOpen(false)
    handleGoToLesson(lesson, index)
    showChat?.(false)
  }

  const {
    previewMode,
    multimediaProgress: { finished },
    canAnswer,
    validations: {
      lesson: { everyEvaluationsHasResponse, everyTasksHasResponse },
    },
  } = useCourse()

  const validateLessons = !previewMode && canAnswer

  return (
    <div>
      <div className={`${styles.sidebarCollapseTrigger} ${styles.onlyMobile}`}>
        <div
          className={`${styles.sidebarButton} d-flex align-items-center px-4`}
          onClick={() =>
            onEvaluation ? setOnEvaluation(false) : setOpen(!open)
          }
        >
          {onEvaluation && <FontAwesomeIcon icon={faChevronLeft} />}
          <span className="p-2">
            {t(`components.courseSidebar.${onEvaluation ? 'back' : 'lessons'}`)}
          </span>
          {!onEvaluation && (
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
          )}
        </div>
      </div>
      <div
        className={`${styles.sidebarContainer} ${styles.onlyDesktop} ${
          open && styles.open
        }`}
      >
        <div className={styles.sidebarContent}>
          {onEvaluation && (
            <BackButton onClick={() => setOnEvaluation(false)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('components.courseSidebar.backToLesson')}
            </BackButton>
          )}
          <CourseName>
            {evaluation?.completionDate
              ? t('components.courseSidebar.reviewOfAnswers')
              : course.name}
          </CourseName>
          {onEvaluation ? (
            <EvaluationDetails evaluation={evaluation as IEvaluationResume} />
          ) : (
            <>
              <LessonsCount>
                {`${lessons.length} ${t(
                  'components.courseSidebar.lessonsCount'
                )}`}
              </LessonsCount>
              <div className={styles.links}>
                {!hideResume && (
                  <a
                    onClick={() => history.push(`/courses/${course.id}`)}
                    className={`${styles.navbarButton} `}
                  >
                    <FontAwesomeIcon icon={faBookOpen} />
                    <span className={styles.navbarButtonText}>
                      {t('components.courseSidebar.backToResume')}
                    </span>
                  </a>
                )}
                {endUser && (
                  <div className="mt-2">
                    <a
                      onClick={() => showChat(true)}
                      className={`${styles.navbarButton} ${
                        chatActive ? styles.chatActive : ''
                      } position-relative`}
                    >
                      <div
                        className={newMessages > 0 ? styles.newMessage : ''}
                      />
                      <FontAwesomeIcon icon={faCommentsAlt} />
                      <span className={styles.navbarButtonText}>
                        {t('components.courseSidebar.messages')}
                      </span>
                    </a>
                  </div>
                )}
              </div>
              <div
                id={'lessonsContainer'}
                className={styles.lessonsContainer}
                style={{ height: `calc(100% - ${lessonsContainerHeight}px)` }}
              >
                {lessons?.map((lesson, index) => {
                  const isNextLesson = lesson.order > currentLesson.order
                  const lessonId = lesson.id || 0
                  const previousLessons = lessons.slice(0, index)
                  const previousFinished = previousLessons.every(({ id }) => {
                    const assignedLesson = courseProgressByLesson.get(id || 0)
                    return (
                      assignedLesson?.state === AssignedLessonStateEnum.Finished
                    )
                  })
                  return (
                    <a
                      id={`row-${index}`}
                      key={index}
                      className={`d-block ${styles.lesson} ${
                        validateLessons &&
                        isNextLesson &&
                        !previousFinished &&
                        !ALLOW_SKIP_TO_NEXT_LESSON
                          ? ` ${styles.disabled}`
                          : ''
                      }`}
                      onClick={() => {
                        if (validateLessons) {
                          if (isNextLesson) {
                            if (!ALLOW_SKIP_TO_NEXT_LESSON && !previousFinished)
                              return
                            if (!finished) return onIncompleteMultimedia?.()
                            if (!everyTasksHasResponse)
                              return onIncompleteTasks?.()
                            if (!everyEvaluationsHasResponse)
                              return onIncompleteEvaluations?.()
                          }
                        }
                        handleClick(lesson, index)
                      }}
                    >
                      {index + 1 < lessons.length && (
                        <div className={styles.lessonSeparator} />
                      )}
                      <LessonNavigation
                        id={lessonId}
                        lessonName={lesson.name}
                        index={index}
                        lessonState={courseProgressByLesson.get(lessonId)}
                        currentLesson={currentLesson}
                      />
                    </a>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CourseSidebard
